<style lang="less" scoped src="@/assets/less/manage-space/base.less"></style>

<template>
    <div>
        <!-- search and actions start -->
        <div class="px-sm-4 d-flex flex-column flex-sm-row align-items-center justify-content-between">
            <CoInput
                v-model="tableFilter"
                type="search"
                icon="search"
                :placeholder="$t('placeholders.search')"
                :aria-label="$t('labels.search')"
                no-border
                background="white"
                class="align-self-sm-start mb-2 mb-sm-0 mr-sm-3"
            />

            <div class="d-block d-sm-flex">
                <CoButton variant="primary" class="m-1 my-sm-0 mr-sm-0" @click="createResource">
                    {{ $t('labels.create') }}
                </CoButton>
            </div>
        </div>
        <!-- search and actions end -->

        <div class="px-sm-4">
            <div class="mt-4">
                <CoTable
                    :loading="loading"
                    :items="filteredResources"
                    :columns="columns"
                    :itemsPerPage="20"
                    striped
                    clickable-rows
                    @clickedRow="editResource"
                >
                    <template v-slot:Name="{ item }">
                        {{ item.Name }}
                    </template>
                    <template v-slot:Category="{ item }">
                        <span v-if="item.Category">
                            {{ item.Category.Name }}
                        </span>
                    </template>
                    <template v-slot:Actions="{ item }">
                        <!-- Keep destructive actions scoped to the affected resource row. -->
                        <div @click.stop class="d-inline-block d-flex align-items-center justify-content-end">
                            <CoTippy :text="$t('labels.duplicate')">
                                <CoRoundButton
                                    icon="copy"
                                    variant="icon"
                                    :aria-label="`${$t('labels.duplicate')} ${item.Name}`"
                                    @click.stop="
                                        duplicateResource({
                                            ...item,
                                            ID: null,
                                            Name: `${item.Name} ${$t('labels.copy')}`,
                                        })
                                    "
                                />
                            </CoTippy>
                            <CoTippy :text="$t('labels.delete')">
                                <CoConfirmation
                                    :message="$t('messages.deleteResourceWarning')"
                                    :confirmLabel="$t('labels.delete')"
                                    :cancelLabel="$t('labels.cancel')"
                                    confirm-variant="danger"
                                    @confirm="deleteResource(item)"
                                >
                                    <CoRoundButton
                                        icon="trash"
                                        variant="icon"
                                        :disabled="deleting"
                                        :aria-label="`${$t('labels.delete')} ${item.Name}`"
                                    />
                                </CoConfirmation>
                            </CoTippy>
                        </div>
                    </template>
                </CoTable>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { cloneDeep } from 'lodash';
import CoTippy from '@/components/Atoms/co-tippy/CoTippy.vue';
import CoButton from '../../../components/Atoms/co-button/CoButton.vue';
import CoRoundButton from '../../../components/Atoms/co-round-button/CoRoundButton.vue';
import CoConfirmation from '../../../components/Molecules/co-confirmation/CoConfirmation.vue';
import CoInput from '../../../components/Molecules/co-input/CoInput.vue';
import CoTable from '../../../components/Molecules/co-table/CoTable.vue';
import EventBus from '../../../eventBus';

export default {
    name: 'BookingResources',
    components: { CoButton, CoConfirmation, CoInput, CoRoundButton, CoTable, CoTippy },
    data() {
        return {
            objects: [],
            tableFilter: '',
            loading: false,
            deleting: false,
            categories: [],
        };
    },
    computed: {
        columns() {
            return [
                {
                    key: 'Name',
                    title: this.$t('labels.name'),
                },
                {
                    key: 'Capacity',
                    title: this.$t('labels.capacity'),
                },
                {
                    key: 'Category',
                    title: this.$t('labels.category'),
                },
                {
                    key: 'Actions',
                    title: this.$t('labels.actions'),
                    notSortable: true,
                    style: 'width: 8rem; text-align: right;',
                },
            ];
        },
        filteredResources() {
            if (!this.tableFilter) {
                return this.objects;
            }

            const searchTerm = this.tableFilter.toLowerCase();

            // Keep filtering explicit so the shared table receives already normalized booking resource data.
            return this.objects.filter((resource) => {
                const categoryName = resource.Category && resource.Category.Name ? resource.Category.Name : '';

                return [resource.Name, String(resource.Capacity || ''), categoryName]
                    .filter(Boolean)
                    .some((value) => value.toLowerCase().includes(searchTerm));
            });
        },
    },
    created() {
        this.getCategories();
    },
    methods: {
        duplicateResource(resource) {
            if (!resource) {
                console.error('Resource not loaded');
                return;
            }
            const obj = cloneDeep(resource);

            this.$store.state.resourceDuplicate = obj;
            this.$router.push('/admin/booking/resource/create');
        },
        editResource(index, resource) {
            if (!resource) {
                return;
            }

            this.$router.push(`/admin/booking/resource/edit/${resource.ID}`);
        },
        deleteResource(resource) {
            if (!resource || this.deleting) {
                return;
            }

            this.deleting = true;
            const data = {};

            axios({
                method: 'DELETE',
                url: `/admin/booking/resource/${resource.ID}`,
                data,
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then(() => {
                    const index = this.objects.findIndex((item) => item.ID === resource.ID);
                    if (index !== -1) {
                        this.objects.splice(index, 1);
                    }
                    this.deleting = false;
                })
                .catch(() => {
                    this.deleting = false;
                    EventBus.$emit('ERROR', {
                        Message: this.$t('errors.failedGeneric'),
                        Details: '',
                    });
                });
        },

        listResources() {
            this.loading = true;
            axios({
                method: 'GET',
                url: '/admin/booking/resource/list',
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    this.loading = false;
                    // Treat a first-use response without resources as an empty list instead of a request failure.
                    const resources =
                        response.data && Array.isArray(response.data.Resources) ? response.data.Resources : [];
                    this.objects = resources.map((element) => {
                        const category = this.categories.find(
                            (item) => item.Resources && item.Resources.includes(element.ID)
                        );
                        return {
                            ...element,
                            Category: category,
                        };
                    });
                })
                .catch(() => {
                    this.loading = false;
                    EventBus.$emit('ERROR', {
                        Message: this.$t('errors.failedGeneric'),
                        Details: '',
                    });
                });
        },
        createResource() {
            this.$router.push('/admin/booking/resource/create');
        },

        getCategories() {
            axios({
                method: 'GET',
                url: '/admin/booking/category/list',
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((response) => {
                    if (response.data && response.data.Categories) {
                        this.categories = response.data.Categories;
                    }
                    this.listResources();
                })
                .catch(() => {
                    this.listResources();
                    EventBus.$emit('ERROR', {
                        Message: this.$t('errors.failedGeneric'),
                        Details: '',
                    });
                });
        },
    },
};
</script>
